import type { PaymentMethod } from '@/api/bookings';
import { refundMethods } from '@core/bookings/payment';
import { humanizeEnum } from '@core/format/text';

/**
 * How the deposit is going back.
 *
 * <p>
 * <strong>The server used to assume cash and nobody could say otherwise.</strong> That read as
 * harmless while the refund row was only ever shown back on the booking it belongs to — the figure
 * was right, only the instrument was invented. It stops being harmless the moment anything sums the
 * cash side: a refund handed back over UPI and recorded as cash takes money out of a drawer it was
 * never in, the till does not reconcile at close, and nothing on the record marks which row is the
 * lie. See `docs/Cash_Ledger_Design.md`.
 * </p>
 *
 * <p>
 * <strong>`Adjustment` is not offered, and that is this control being stricter than the server.</strong>
 * It means no money moved, which is the one thing a refund is not. Setting a refund against
 * something the customer still owes is a real counter habit, but nothing in the model nets the two
 * yet — so the server accepts the value rather than enforcing a rule it has no arithmetic for, and
 * the counter simply is not shown a door that leads nowhere.
 * </p>
 */
export function RefundMethodChips({
  value,
  onChange,
  disabled = false,
}: {
  value: PaymentMethod;
  onChange: (next: PaymentMethod) => void;
  disabled?: boolean;
}) {
  return (
    <div>
      <p className="mb-1.5 text-[12px] font-medium text-gray-900">Refunded how?</p>
      <div role="group" aria-label="Refund method" className="flex flex-wrap gap-1.5">
        {refundMethods.map((option) => (
          <button
            key={option}
            type="button"
            disabled={disabled}
            onClick={() => onChange(option)}
            aria-pressed={option === value}
            className={`cursor-pointer rounded border px-2.5 py-1 text-[12px] disabled:cursor-not-allowed disabled:opacity-40 ${
              option === value
                ? 'bg-dark border-dark text-white'
                : 'border-border-color hover:bg-light bg-white text-gray-900'
            }`}
          >
            {humanizeEnum(option)}
          </button>
        ))}
      </div>
    </div>
  );
}
